import { FC, useEffect, useState } from 'react'
import { ApplicationFrame } from '../../components/ApplicationFrame'
import stockhubLogo from '../../assets/stockhub-pos/logo.png'
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormControl,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
  FormHelperText,
  FormControlLabel,
  Select,
  MenuItem,
} from '@mui/material'
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined'
import {
  useClientApiCall,
  useClientApiMutation,
} from '../../hooks/useClientApiCall'
import { useSearchParams } from 'react-router-dom'
import { ApplicationLoadingScreen } from '../../components/ApplicationLoadingScreen'
import { toast } from 'react-toastify'
import IOSSwitch from '../../components/IOSSwitch'
/**
 * Uber Eats Application
 */
export const StockhubPOS: FC = () => {
  const [params] = useSearchParams()
  const session = params.get('session')!
  const connection = useClientApiCall<any>(session, 'connection')

  if (connection.isLoading) {
    return <ApplicationLoadingScreen />
  }

  /**
   *
   */
  return (
    <ApplicationFrame heroImage={stockhubLogo} backgroundColor="white">
      {/* Centralised Title with padding */}
      <Typography variant="h4" align="center" sx={{ p: 2 }}>
        Stockhub
      </Typography>
      {<ConfigureCredentials connection={connection} />}
    </ApplicationFrame>
  )
}

export const ConfigureCredentials: FC<any> = ({ connection }) => {
  const [params] = useSearchParams()
  const session = params.get('session')!
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [productId, setProductId] = useState('')
  const [storeLink, setStoreLink] = useState('')
  const [catalogSyncStatus, setCatalogSynStatus] = useState('')
  const [autoSyncCatalog, setAutoSyncCatalog] = useState(false)
  const [autoSyncCatalogInterval, setautoSyncCatalogInterval] =
    useState<any>(60)
  const syncCatalog = useClientApiMutation<any>(session, 'catalog-pull')
  const updateConnection = useClientApiMutation<any>(
    session,
    'connection',
    'PATCH'
  )
  const isFormDisabled = !storeLink //!username || !password ||

  // When the user clicks the connect button, we will call this function
  const onSubmit = (body: any) => {
    updateConnection.mutate(body, {
      onSuccess: (data: any) => {
        toast.success(data?.message)
        setCatalogSynStatus('INPROGRESS')
        // syncCatalogHandler()
        connection.refetch()
      },
    })
  }
  const syncCatalogHandler = () => {
    syncCatalog.mutate({}, { onSuccess: () => connection.refetch() })
  }

  const handlePaste = (setState: Function) => {
    navigator.clipboard.readText().then((text) => {
      text = text.replace(/\s/g, '')
      setState(text)
    })
  }

  const handleToggleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setAutoSyncCatalog(checked)
  }

  useEffect(() => {
    setStoreLink(connection.data?.connection?.storeLink ?? '')
    setUsername(connection.data?.connection?.username ?? '')
    setPassword(connection.data?.connection?.password ?? '')
    setProductId(connection.data?.connection?.productId ?? '')
    setAutoSyncCatalog(connection.data?.connection?.autoSyncCatalog ?? false)
    setautoSyncCatalogInterval(
      connection.data?.connection?.autoSyncCatalogInterval ?? 60
    )
    setCatalogSynStatus(connection.data?.catalogSyncStatus)
  }, [connection.data])

  const getBaseUrl = (storeLink: string) => {
    const parsedUrl = new URL(storeLink)
    return `${parsedUrl.protocol}//${parsedUrl.hostname}`
  }

  const InputProps = (fn: any) => ({
    endAdornment: (
      <InputAdornment position="end">
        <Tooltip title="Paste">
          <IconButton aria-label="Paste" onClick={() => handlePaste(fn)}>
            <ContentPasteOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </InputAdornment>
    ),
  })
  return (
    <Box pb={2}>
      <Grid container spacing={3} p={2}>
        <Grid item sm={12} xs={12}>
          <FormControl variant="outlined" fullWidth>
            <TextField
              autoFocus
              fullWidth
              helperText="Enter the link of the website"
              label="Store Url"
              placeholder="https://example.co.uk"
              value={storeLink}
              onChange={(event) => setStoreLink(event.target.value)}
              InputProps={!storeLink ? InputProps(setStoreLink) : {}}
            />
          </FormControl>
        </Grid>
        <Grid item sm={12} xs={12}>
          {' '}
          <FormControl variant="outlined" fullWidth>
            <TextField
              helperText={'Enter the username'}
              label="Username"
              fullWidth
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              InputProps={!username ? InputProps(setUsername) : {}}
            />
          </FormControl>
        </Grid>
        <Grid item sm={12} xs={12}>
          {' '}
          <FormControl variant="outlined" fullWidth>
            <TextField
              helperText={'Enter the password '}
              fullWidth
              label="Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              InputProps={!password ? InputProps(setPassword) : {}}
            />
          </FormControl>
        </Grid>
        <Grid item sm={12} xs={12}>
          {' '}
          <FormControl variant="outlined" fullWidth>
            <TextField
              helperText={'Enter Product Id to map customer Id'}
              fullWidth
              label="Product Id"
              value={productId}
              onChange={(event) => setProductId(event.target.value)}
              InputProps={!productId ? InputProps(setProductId) : {}}
            />
          </FormControl>
        </Grid>

        {/* Label for Auto Sync Catalog */}
        <Grid item xs={8}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              pl: 1,
            }}
          >
            <Typography variant="body1">Auto sync catalog</Typography>
          </Box>
        </Grid>

        {/* Toggle Switch */}
        <Grid item xs={4} container justifyContent="flex-end">
          <FormControlLabel
            control={
              <IOSSwitch
                checked={autoSyncCatalog}
                onChange={handleToggleChange}
              />
            }
            label=""
          />
        </Grid>

        {/* Label for Auto Sync Interval */}
        <Grid item xs={8}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              pl: 1,
            }}
          >
            <Typography variant="body1">Auto sync interval</Typography>
          </Box>
        </Grid>

        {/* Interval Selector */}
        <Grid
          item
          xs={4}
          container
          justifyContent="flex-end"
          alignItems="center"
        >
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={autoSyncCatalogInterval}
              onChange={(e) => setautoSyncCatalogInterval(e.target.value)}
            >
              <MenuItem value={10}>15 mins</MenuItem>
              <MenuItem value={30}>30 mins</MenuItem>
              <MenuItem value={45}>45 mins</MenuItem>
              <MenuItem value={60}>60 mins</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item sm={12} xs={12}>
          {' '}
          <Button
            variant="outlined"
            fullWidth
            color="primary"
            onClick={() =>
              onSubmit({
                connection: {
                  username,
                  password,
                  productId,
                  storeLink: getBaseUrl(storeLink),
                  autoSyncCatalog,
                  autoSyncCatalogInterval,
                },
              })
            }
            disabled={isFormDisabled}
          >
            {'UPDATE'}
            {updateConnection.isLoading ? (
              <CircularProgress size={20} sx={{ ml: 2 }} />
            ) : null}
          </Button>
        </Grid>

        {connection.data?.connection?.storeLink ? (
          <Grid item sm={12} xs={12}>
            {' '}
            <Button
              variant="outlined"
              fullWidth
              color="secondary"
              onClick={() => {
                setCatalogSynStatus('INPROGRESS')
                syncCatalogHandler()
              }}
            >
              {'SYNC CATALOG'}
              {catalogSyncStatus === 'INPROGRESS' ? (
                <CircularProgress size={20} color="secondary" sx={{ ml: 2 }} />
              ) : null}
            </Button>
            {connection.data.catalogLastUpdated ? (
              <>
                {' '}
                <FormHelperText>
                  Last synced:
                  {new Date(connection.data.catalogLastUpdated).toLocaleString(
                    'en-US',
                    {
                      timeZone: connection.data?.locationTimezone || 'UTC',
                    }
                  )}
                  , Status:{catalogSyncStatus}
                </FormHelperText>
              </>
            ) : (
              <></>
            )}
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </Box>
  )
}
